import { useEffect, useState } from 'react';
import Button from '@/components/base/Button';
import Modal from '@/components/base/Modal';
import { africanMarkets } from '@/mocks/markets';
import { formatMoney } from '@/utils/format';
import type { CampaignCreator } from '@/types/portal';

export type RespondInput =
  | { action: 'accepted' | 'declined' }
  | { action: 'counterOffer'; proposedBudget: number; currency: string; message: string };

interface RespondModalProps {
  entry: CampaignCreator | null;
  /** The campaign's per-creator budget and currency, shown as the starting point. */
  budgetPerCreator: number;
  currency: string;
  saving: boolean;
  error: string;
  onClose: () => void;
  onRespond: (input: RespondInput) => void;
}

const inputClass =
  'h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none';

/** The live portal's negotiation panel (creator-detail): accept, decline, or send a counter-offer. */
export default function RespondModal({ entry, budgetPerCreator, currency, saving, error, onClose, onRespond }: RespondModalProps) {
  const [countering, setCountering] = useState(false);
  const [amount, setAmount] = useState('');
  const [offerCurrency, setOfferCurrency] = useState(currency);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!entry) return;
    setCountering(false);
    setAmount('');
    setOfferCurrency(currency);
    setMessage('');
  }, [entry, currency]);

  if (!entry) return null;
  const offer = entry.counterOffer;
  const name = entry.creator?.name ?? 'this creator';
  const currencies = [...new Set([currency, ...africanMarkets.map((market) => market.currency)])];
  const amountValid = amount.trim() !== '' && Number(amount) >= 0;

  return (
    <Modal
      open
      onClose={onClose}
      icon="ri-chat-3-line"
      title={`Respond to ${name}`}
      subtitle="The creator is told by email, SMS and in the Twiva app"
      widthClassName="max-w-md"
      footer={
        countering ? (
          <>
            <Button variant="outline" onClick={() => setCountering(false)}>
              Back
            </Button>
            <Button
              icon="ri-send-plane-line"
              disabled={!amountValid || saving}
              onClick={() => onRespond({ action: 'counterOffer', proposedBudget: Number(amount), currency: offerCurrency, message: message.trim() })}
            >
              {saving ? 'Sending…' : 'Send counter-offer'}
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline" icon="ri-close-line" disabled={saving} onClick={() => onRespond({ action: 'declined' })}>
              Decline
            </Button>
            <Button variant="outline" icon="ri-exchange-line" disabled={saving} onClick={() => setCountering(true)}>
              Counter-offer
            </Button>
            <Button icon="ri-check-line" disabled={saving} onClick={() => onRespond({ action: 'accepted' })}>
              Accept offer
            </Button>
          </>
        )
      }
    >
      <div className="space-y-4">
        {error ? (
          <p role="alert" className="flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-sm text-primary-800">
            <i className="ri-error-warning-line mt-0.5" />
            {error}
          </p>
        ) : null}

        <div className="rounded-md border border-background-200 bg-background-100 px-4 py-3">
          <p className="text-[11px] font-label uppercase tracking-wider text-foreground-400">Creator's counter-offer</p>
          {offer ? (
            <>
              <p className="mt-1 font-heading text-lg font-semibold text-foreground-950">{formatMoney(offer.amount, offer.currency)}</p>
              {offer.message ? <p className="mt-1 text-sm text-foreground-600">{offer.message}</p> : null}
            </>
          ) : (
            <p className="mt-1 text-sm text-foreground-600">
              No counter-offer is recorded. Their current rate on the roster is {formatMoney(entry.rate, entry.currency)}.
            </p>
          )}
          <p className="mt-2 text-xs text-foreground-500">Campaign budget: {formatMoney(budgetPerCreator, currency)} per creator</p>
        </div>

        {countering ? (
          <>
            <div className="grid grid-cols-[minmax(0,1fr)_120px] gap-3">
              <div>
                <label htmlFor="respond-amount" className="mb-1.5 block text-xs font-label font-medium text-foreground-700">
                  Your offer
                </label>
                <input
                  id="respond-amount"
                  type="number"
                  min={0}
                  step="0.01"
                  className={inputClass}
                  value={amount}
                  placeholder={String(budgetPerCreator || '')}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="respond-currency" className="mb-1.5 block text-xs font-label font-medium text-foreground-700">
                  Currency
                </label>
                <select id="respond-currency" className={inputClass} value={offerCurrency} onChange={(e) => setOfferCurrency(e.target.value)}>
                  {currencies.map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label htmlFor="respond-message" className="mb-1.5 block text-xs font-label font-medium text-foreground-700">
                Message (optional)
              </label>
              <textarea
                id="respond-message"
                rows={3}
                maxLength={1000}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Explain your counter-offer…"
                className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
              />
            </div>
          </>
        ) : (
          <p className="text-sm text-foreground-600">
            Accepting moves {name} on to the contract. Declining ends their part in the campaign.
          </p>
        )}
      </div>
    </Modal>
  );
}
